import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { authResetPassword } from '../lib/api';

const MIN_LEN = 10;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < MIN_LEN) {
      setErr(`Wachtwoord minimaal ${MIN_LEN} tekens.`);
      return;
    }
    if (password !== password2) {
      setErr('Wachtwoorden komen niet overeen.');
      return;
    }
    setBusy(true);
    try {
      const r = await authResetPassword(token, password);
      if (!r.ok) {
        setErr(r.message ?? r.error);
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/inloggen', { replace: true }), 2000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        path="/wachtwoord-reset"
        title="Nieuw wachtwoord"
        description="Stel je ComfortTool™-wachtwoord opnieuw in via je resetlink."
        keywords="ComfortTool wachtwoord reset"
        noindex
      />
      <div className="mx-auto max-w-md px-4 py-12">
        <h1 className="text-2xl font-bold text-ct-text">Nieuw wachtwoord</h1>
        {!token || token.length < 32 ? (
          <p className="mt-4 text-sm text-ct-muted">
            Deze link is ongeldig of incompleet. Vraag een{' '}
            <Link to="/wachtwoord-vergeten" className="text-ct-accent hover:underline">
              nieuwe resetlink
            </Link>{' '}
            aan.
          </p>
        ) : done ? (
          <p className="mt-4 text-sm text-ct-muted" role="status">
            Je wachtwoord is bijgewerkt. Je wordt doorgestuurd naar inloggen…
          </p>
        ) : (
          <form className="mt-8 space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div>
              <label className="block text-sm font-medium text-ct-text" htmlFor="reset-pw1">
                Nieuw wachtwoord (min. {MIN_LEN} tekens)
              </label>
              <input
                id="reset-pw1"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_LEN}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ct-text" htmlFor="reset-pw2">
                Bevestig wachtwoord
              </label>
              <input
                id="reset-pw2"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_LEN}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
              />
            </div>
            {err ? (
              <p className="text-sm text-red-400" role="alert">
                {err}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-ct-accent py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {busy ? 'Bezig…' : 'Opslaan'}
            </button>
          </form>
        )}
        <p className="mt-6 text-center text-sm text-ct-muted">
          <Link to="/inloggen" className="font-semibold text-ct-accent hover:underline">
            Naar inloggen
          </Link>
        </p>
      </div>
    </>
  );
}
